/**
 * Converts CKEditor HTML output to email-safe HTML with inline styles.
 */
export function convertToEmailSafeHtml(html: string): string {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Unwrap <figure> elements
    tempDiv.querySelectorAll('figure').forEach(figure => {
        const parent = figure.parentNode;
        while (figure.firstChild) parent?.insertBefore(figure.firstChild, figure);
        figure.remove();
    });

    // Helper to set style and remove class
    const styleAndClean = (el: Element, style: string, preserveStyle = false) => {
        const existing = preserveStyle ? el.getAttribute('style') || '' : '';
        el.setAttribute('style', existing ? `${existing}; ${style}`.replace(/^; /, '') : style);
        el.removeAttribute('class');
    };

    // Tables
    tempDiv.querySelectorAll('table').forEach(table => {
        table.setAttribute('style', 'border-collapse: collapse; width: 100%; margin-bottom: 1em; font-family: Arial, sans-serif; border: 1px solid #ddd;');
        table.setAttribute('border', '1');
        table.setAttribute('cellpadding', '8');
        table.setAttribute('cellspacing', '0');
        table.removeAttribute('class');

        table.querySelectorAll('td, th').forEach(cell =>
            styleAndClean(cell, 'border: 1px solid #ddd; padding: 8px; text-align: left;', true));
        table.querySelectorAll('th').forEach(th =>
            styleAndClean(th, 'background-color: #f2f2f2; font-weight: bold;', true));
        table.querySelectorAll('tbody, thead, tr').forEach(el => el.removeAttribute('class'));
    });

    // Convert <mark> to <span> with inline background
    const markColors: Record<string, string> = {
        'marker-green': '#63f963',
        'marker-pink': '#fc7999',
        'marker-blue': '#72cdfd'
    };

    tempDiv.querySelectorAll('mark').forEach(mark => {
        const span = document.createElement('span');
        span.innerHTML = mark.innerHTML;
        const className = mark.getAttribute('class') || '';
        const bgColor = Object.entries(markColors).find(([key]) => className.includes(key))?.[1] || '#fdfd77';
        span.setAttribute('style', `background-color: ${bgColor}; padding: 2px 4px;`);
        mark.parentNode?.replaceChild(span, mark);
    });

    // Bulk styling with simple selectors
    const styleMap: [string, string, boolean?][] = [
        ['ul, ol', 'margin: 0 0 1em 0; padding-left: 40px;'],
        ['li', 'margin-bottom: 4px;'],
        ['p', 'margin: 0 0 1em 0;', true],
        ['blockquote', 'border-left: 4px solid #ccc; margin: 0 0 1em 0; padding-left: 16px; color: #666; font-style: italic;'],
        ['h1, h2, h3, h4, h5, h6', 'margin: 0 0 0.5em 0; font-weight: bold;'],
        ['a', 'color: #0066cc; text-decoration: underline;'],
        ['strong, b', 'font-weight: bold;'],
        ['hr', 'border: none; border-top: 1px solid #ddd; margin: 1em 0;'],
    ];

    styleMap.forEach(([selector, style, preserveExisting]) =>
        tempDiv.querySelectorAll(selector).forEach(el => styleAndClean(el, style, preserveExisting)));

    // Handle em/i separately (skip font-awesome icons)
    tempDiv.querySelectorAll('em, i').forEach(e => {
        if (!e.classList.contains('fas') && !e.classList.contains('fa')) {
            e.setAttribute('style', 'font-style: italic;');
        }
    });

    return tempDiv.innerHTML;
}